import React from "react";

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f4f8ff 0%, #eef7f3 100%)",
    padding: "32px 16px",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    color: "#1f2937",
  },
  container: {
    maxWidth: "980px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "24px",
    boxShadow: "0 20px 60px rgba(15, 23, 42, 0.10)",
    overflow: "hidden",
    border: "1px solid #e5e7eb",
  },
  hero: {
    background: "linear-gradient(135deg, #0f766e 0%, #2563eb 100%)",
    color: "#fff",
    padding: "40px 28px",
  },
  title: {
    margin: 0,
    fontSize: "clamp(28px, 4vw, 42px)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    marginTop: "12px",
    fontSize: "16px",
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.92)",
    maxWidth: "760px",
  },
  badgeRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "18px",
  },
  badge: {
    background: "rgba(255,255,255,0.16)",
    border: "1px solid rgba(255,255,255,0.22)",
    padding: "8px 14px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 600,
  },
  content: {
    padding: "28px",
  },
  noticeBox: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "22px",
    color: "#1e3a8a",
    lineHeight: 1.7,
  },
  section: {
    marginBottom: "18px",
    background: "#fcfcfd",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    padding: "22px",
  },
  heading: {
    margin: "0 0 12px",
    fontSize: "22px",
    fontWeight: 700,
    color: "#111827",
  },
  text: {
    margin: "0 0 12px",
    lineHeight: 1.8,
    fontSize: "15.5px",
    color: "#374151",
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "#374151",
    lineHeight: 1.8,
    fontSize: "15.5px",
  },
  listItem: {
    marginBottom: "8px",
  },
  subCard: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    padding: "16px",
    marginTop: "14px",
  },
  contactBox: {
    background: "linear-gradient(180deg, #f0fdf4 0%, #ecfeff 100%)",
    border: "1px solid #bbf7d0",
    borderRadius: "18px",
    padding: "22px",
    marginTop: "10px",
  },
  footer: {
    padding: "20px 28px 28px",
    borderTop: "1px solid #e5e7eb",
    color: "#6b7280",
    fontSize: "14px",
    lineHeight: 1.7,
  },
};

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.hero}>
          <h1 style={styles.title}>Privacy Policy</h1>
          <p style={styles.subtitle}>
            This Privacy Policy describes how Healthrely collects, uses, stores,
            shares, and protects personal information when you use our healthcare,
            consultation, medicine ordering, lab booking, and related digital services.
          </p>
        </div>

        <div style={styles.content}>
          <div style={styles.noticeBox}>
            By using our platform, creating an account, booking a consultation, placing
            a medicine order, booking a lab test, or submitting your profile information,
            you acknowledge that you have read this Privacy Policy and understand how your
            personal data is handled in connection with our services.
          </div>

          <section style={styles.section}>
            <h2 style={styles.heading}>1. Who We Are</h2>
            <p style={styles.text}>
              Healthrely is a healthcare services platform that may provide features
              such as doctor consultations, medicine ordering, lab test booking, hospital
              discovery, account management, reminders, and customer support through its
              mobile application, website, and related channels.
            </p>
            <p style={styles.text}>
              For the purposes of applicable privacy law, Healthrely acts as the
              entity responsible for deciding how and why your personal data is processed
              in connection with these services.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>2. Information We Collect</h2>
            <ul style={styles.list}>
              <li style={styles.listItem}>
                <strong>Identity and profile data:</strong> name, gender, date of birth,
                age, profile details, and account credentials.
              </li>
              <li style={styles.listItem}>
                <strong>Contact data:</strong> mobile number, email address, and customer
                communication preferences.
              </li>
              <li style={styles.listItem}>
                <strong>Location data you provide:</strong> pincode, city, state, delivery
                address, and similar service-location details.
              </li>
              <li style={styles.listItem}>
                <strong>Healthcare and service data:</strong> doctor consultation details,
                medicine orders, prescriptions, lab bookings, uploaded reports, symptoms,
                care preferences, and related records submitted by you or on your behalf.
              </li>
              <li style={styles.listItem}>
                <strong>Transaction data:</strong> booking details, invoices, payment status,
                refunds, and order history.
              </li>
              <li style={styles.listItem}>
                <strong>Technical and usage data:</strong> device information, operating system,
                app version, IP address, log files, session activity, crash reports, and usage analytics.
              </li>
              <li style={styles.listItem}>
                <strong>Support and communication data:</strong> messages, feedback, grievances,
                call records where lawfully permitted, and support requests.
              </li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>3. How We Collect Data</h2>
            <ul style={styles.list}>
              <li style={styles.listItem}>Directly from you when you register, update your profile, or use our services.</li>
              <li style={styles.listItem}>From transactions and service requests such as consultations, orders, and test bookings.</li>
              <li style={styles.listItem}>Automatically through app logs, cookies, pixels, SDKs, and analytics tools where applicable.</li>
              <li style={styles.listItem}>From healthcare providers, laboratories, pharmacies, hospitals, payment partners, or delivery partners involved in fulfilling your request.</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>4. Why We Use Your Data</h2>
            <ul style={styles.list}>
              <li style={styles.listItem}>To create, verify, and maintain your account.</li>
              <li style={styles.listItem}>To schedule consultations and connect you with doctors or healthcare professionals.</li>
              <li style={styles.listItem}>To process medicine orders, deliveries, lab bookings, and related service requests.</li>
              <li style={styles.listItem}>To provide reminders, notifications, invoices, support messages, and service updates.</li>
              <li style={styles.listItem}>To improve platform performance, safety, service design, and user experience.</li>
              <li style={styles.listItem}>To prevent fraud, abuse, unauthorized access, and other harmful activity.</li>
              <li style={styles.listItem}>To comply with legal, regulatory, tax, audit, medical documentation, and contractual obligations.</li>
            </ul>

            <div style={styles.subCard}>
              <p style={styles.text}>
                We process healthcare-related information only to the extent reasonably necessary
                for delivering requested services, maintaining records, handling support, ensuring
                platform safety, and meeting legal or regulatory requirements.
              </p>
            </div>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>5. Consent and Choice</h2>
            <p style={styles.text}>
              Where required by applicable law, we rely on your consent to collect and process
              personal data. Your consent may be taken when you register, update your profile,
              upload documents, request a consultation, place an order, or otherwise submit data
              through our services.
            </p>
            <p style={styles.text}>
              You may withdraw consent for specific processing activities, subject to legal,
              safety, operational, medical-record, fraud-prevention, or service-delivery limitations.
              Withdrawal of consent may affect our ability to provide some or all services to you.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>6. Data Sharing</h2>
            <p style={styles.text}>We may share your data only where necessary and permitted by law, including with:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Doctors, clinics, laboratories, hospitals, and pharmacies involved in your requested service.</li>
              <li style={styles.listItem}>Payment processors, delivery partners, cloud hosting providers, analytics vendors, and customer support vendors working under contractual safeguards.</li>
              <li style={styles.listItem}>Affiliates, subsidiaries, and authorized business partners where necessary for lawful service operations.</li>
              <li style={styles.listItem}>Regulators, law enforcement agencies, courts, or government authorities when required by applicable law or valid legal process.</li>
              <li style={styles.listItem}>A successor entity in connection with a merger, acquisition, restructuring, financing, or sale of business assets, subject to applicable safeguards.</li>
            </ul>
            <p style={styles.text}>
              We do not sell your personal data as part of our ordinary business operations.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>7. Data Retention</h2>
            <p style={styles.text}>
              We keep personal data only for as long as reasonably necessary for the purposes
              described in this Privacy Policy, including service delivery, recordkeeping,
              payment processing, audits, fraud prevention, dispute resolution, and compliance
              with applicable legal or regulatory requirements.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>8. Security Measures</h2>
            <p style={styles.text}>
              We maintain reasonable technical, administrative, and organizational safeguards
              designed to protect personal data against unauthorized access, loss, misuse,
              alteration, or disclosure. These measures may include access controls, encrypted
              transmission, logging, monitoring, vendor controls, and role-based restrictions.
            </p>
            <p style={styles.text}>
              No digital platform can guarantee absolute security, but we continuously work to
              reduce risk and respond promptly to identified security issues.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>9. Your Rights</h2>
            <p style={styles.text}>Subject to applicable law, you may have the right to:</p>
            <ul style={styles.list}>
              <li style={styles.listItem}>Request access to a summary of personal data we hold about you.</li>
              <li style={styles.listItem}>Request correction, completion, or updating of inaccurate or incomplete data.</li>
              <li style={styles.listItem}>Request deletion or erasure of data, subject to lawful retention requirements.</li>
              <li style={styles.listItem}>Withdraw consent for processing based on consent.</li>
              <li style={styles.listItem}>Request information about categories of third parties with whom your data has been shared, where applicable.</li>
              <li style={styles.listItem}>Use our grievance redressal process for complaints or concerns about personal data handling.</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>10. Children and Guardian Use</h2>
            <p style={styles.text}>
              If our services are used for a child or dependent, the information must be provided
              by a parent, lawful guardian, or authorized caregiver where required by law. We do not
              knowingly process personal data in violation of applicable rules relating to children.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>11. Cookies, SDKs, and Analytics</h2>
            <p style={styles.text}>
              We may use cookies, pixels, software development kits, and analytics tools to keep
              the platform functional, understand performance, reduce technical issues, measure
              engagement, and improve user experience. Where required, permissions or consent will
              be requested through the app, browser, or device.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>12. Third-Party Services</h2>
            <p style={styles.text}>
              Our platform may integrate with third-party providers such as payment gateways,
              laboratories, hospital systems, pharmacies, maps, messaging tools, or customer support
              providers. Their data practices are governed by their own policies to the extent they
              independently control personal data.
            </p>
          </section>

          <section style={styles.section}>
            <h2 style={styles.heading}>13. Changes to This Policy</h2>
            <p style={styles.text}>
              We may revise this Privacy Policy from time to time to reflect changes in law,
              technology, platform features, business operations, or compliance practices.
              The updated version will be posted with a revised effective date.
            </p>
          </section>

          <section style={styles.contactBox}>
            <h2 style={styles.heading}>14. Contact:</h2>
            <p style={styles.text}>
              For privacy questions, rights requests, corrections, withdrawal of consent, or
              grievance-related concerns, please contact:
            </p>
            <p style={styles.text}>
              Email: quaeresocial@gmail.com<br />
              Support Email: quaeresocial@gmail.com<br />
              Phone: 7388869995<br />
            </p>
            <p style={styles.text}>
              We will review and respond to requests within a reasonable period,
              subject to identity verification and applicable legal requirements.
            </p>
          </section>
        </div>

        <div style={styles.footer}>
          This Privacy Policy template should be reviewed by your company’s legal or compliance
          team before production use, especially if your platform stores prescriptions, medical
          records, or consultation notes.
        </div>
      </div>
    </div>
  );
}